import { useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast-v2'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { queryKeys } from 'api/kinerja/tim-penilai-ps/config'
import { Header } from 'app/KinerjaShared/components/Header'
import { TablePenetapan } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan'
import useBasePath from 'app/KinerjaTimPenilaiPSPage/hooks/useBasePath'
import {
  useAPIGetPenetapanPegawai,
  useAPISendPenetapanPegawaiToEkin,
} from 'api/kinerja/tim-penilai-ps/penetapan'
import { useAPIGetPeriodDetail } from 'api/kinerja/pengawas-sekolah/period'
import { humanizeDate } from 'utils/datetime'
import { usePenetapanTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePenetapanTrackEvent'

import type { ActionBulkSendToEkinOptions } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan/ActionBulkSendToEkin'

const PAGE_SIZE = 10

export default function PegawaiListSendToEkin() {
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { period } = usePeriodContext()
  const { basePath } = useBasePath()
  const { trackEvent } = usePenetapanTrackEvent()

  const [page, setPage] = useState(1)

  // Get Penetapan Pegawai List
  const { data: responseData, isFetching } = useAPIGetPenetapanPegawai({
    params: {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      status: 'SENT_TO_PPK',
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })
  const data = useMemo(() => responseData?.data, [responseData?.data])
  const meta = useMemo(() => responseData?.meta, [responseData?.meta])
  const showPagination = meta?.total > PAGE_SIZE
  const isListEmpty = (data?.pengawasSekolah ?? []).length === 0
  const pengawasSekolahIds = (data?.pengawasSekolah ?? []).map(
    ({ pengawasSekolahId }) => pengawasSekolahId
  )

  // Get Period Detail
  const {
    data: periodData,
    isSuccess: isPeriodSuccess,
    isFetching: isPeriodFetching,
  } = useAPIGetPeriodDetail(period.id, {
    staleTime: 60 * 60 * 1000, // 1 hour
  })
  const startSendToEkinDate = useMemo(() => {
    if (isPeriodFetching || !isPeriodSuccess) return '-'

    const formattedDate = humanizeDate(periodData?.penetapan.startDate ?? '-')
    return formattedDate.split(',')[0]
  }, [isPeriodFetching, isPeriodSuccess, periodData?.penetapan.startDate])

  const isOutsidePenetapanDateRange = useMemo(() => {
    const startDate = periodData?.penetapan?.startDate
    const endDate = periodData?.penetapan?.endDate

    if (!startDate || !endDate) return false

    const now = new Date()
    const start = new Date(`${startDate}T00:00:00`)
    const end = new Date(`${endDate}T23:59:59`)

    return now < start || now > end
  }, [periodData?.penetapan?.endDate, periodData?.penetapan?.startDate])

  const isPrintDisabled = isListEmpty
  const isSendDisabled =
    isListEmpty || !isPeriodSuccess || isOutsidePenetapanDateRange

  const resetQueries = () => {
    // Invalidate the get detail (pegawai list) query of the current page
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI,
      {
        period: period.id,
      },
    ])
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_STATUS,
      {
        period: period.id,
      },
    ])
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_PREDICATE,
      {
        period: period.id,
      },
    ])
  }

  // Send Penetapan to Pegawai & E-Kinerja
  const { mutate: sendToEkin, isLoading: isLoadingSendToEkin } =
    useAPISendPenetapanPegawaiToEkin({})
  const handleSendToEkin = (options: ActionBulkSendToEkinOptions = {}) => {
    const { onSuccess, onError } = options
    sendToEkin(
      {
        pengawasSekolahIds,
      },
      {
        onSuccess: () => {
          onSuccess?.()

          trackEvent('tabel pegawai PPK', 'send predikat kinerja')
        },
        onError: () => {
          onError?.()

          // Show toast message
          toast({
            message: 'Gagal kirim Penetapan ke Pegawai. Coba lagi.',
          })
        },
      }
    )
  }

  return (
    <div className="min-h-full px-8 pt-12 pb-20 bg-gray-10">
      <div className="pb-12 mx-auto max-w-screen-2xl">
        <Header
          title="Kirim Penetapan Predikat Kinerja ke Pengawas Sekolah"
          className="mb-12 print:hidden"
        />

        <main>
          <div className="flex justify-between p-6 bg-white rounded-t-lg print:hidden">
            <div className="space-y-2">
              <div className="space-y-1">
                <Text variant="heading-md">
                  Berikut data yang akan dikirim:
                </Text>
                <Text>
                  Data yang tidak muncul di sini dapat Anda kirim di pengiriman
                  berikutnya.
                </Text>
              </div>
              <div className="flex px-4 py-2 border rounded-md w-fit border-success-default bg-surface-success-subdued gap-x-1">
                <Icon color="success">check_circle</Icon>
                <Text color="success">
                  Dikirim mulai dari: {startSendToEkinDate}
                </Text>
              </div>
            </div>

            <div className="flex gap-x-2 flex-0">
              <Button
                color={isPrintDisabled ? 'black' : 'white'}
                className="!text-sm h-fit gap-x-1"
                disabled={isPrintDisabled}
                onClick={() => {
                  window.print()
                }}
              >
                <Icon
                  fontSize="sm"
                  color={isPrintDisabled ? 'disabled' : 'default'}
                >
                  print
                </Icon>
                Cetak
              </Button>
              <TablePenetapan.ActionBulkSendToEkin
                count={meta?.total ?? 0}
                isDisabled={isSendDisabled}
                isSubmitting={isLoadingSendToEkin}
                onSubmit={handleSendToEkin}
                onCloseSuccessDialog={async () => {
                  const isNewActivity = window.history.length <= 2
                  if (isNewActivity) {
                    await router.replace(`${basePath}/penilai/penetapan`)
                  } else {
                    router.back()
                  }

                  resetQueries()
                }}
              />
            </div>
          </div>
          <TablePenetapan.Provider
            data={data}
            meta={meta}
            isFetching={isFetching}
          >
            <div className="space-y-4">
              <TablePenetapan.TableSendToEkin
                nextPageResolver={(pengawasSekolahId) =>
                  `${basePath}/penilai/penetapan/${pengawasSekolahId}`
                }
              />
              {showPagination && (
                <TablePenetapan.Pagination
                  totalItems={meta?.total}
                  pageSize={PAGE_SIZE}
                  currentPage={page}
                  onPageChange={setPage}
                />
              )}
            </div>
          </TablePenetapan.Provider>
        </main>
      </div>
    </div>
  )
}
